import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { useGetKsefOutgoingStatusQuery } from '../Api/ksefOutgoingApi';

interface CountBadgeProps {
  label: string;
  count: number;
  color?: 'primary' | 'warning' | 'error' | 'success' | 'default';
}

const CountBadge: React.FC<CountBadgeProps> = ({ label, count, color = 'default' }) => (
  <Box textAlign="center">
    <Typography variant="h5" fontWeight="bold">
      {count}
    </Typography>
    <Chip label={label} color={color} size="small" />
  </Box>
);

interface KsefOutgoingStatusWidgetProps {
  pollingIntervalMs?: number;
}

const KsefOutgoingStatusWidget: React.FC<KsefOutgoingStatusWidgetProps> = ({
  pollingIntervalMs,
}) => {
  const { data, isLoading, isError, error } = useGetKsefOutgoingStatusQuery(undefined, {
    pollingInterval: pollingIntervalMs,
  });

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Ładowanie statusu faktur wychodzących KSeF…</Typography>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        Błąd ładowania statusu faktur wychodzących KSeF.{' '}
        {extractApiErrorMessage(error, 'Wystąpił nieoczekiwany błąd.')}
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        KSeF — Faktury wychodzące
      </Typography>

      {/* Stany wymagające uwagi operatora */}
      <Stack direction="row" spacing={4} justifyContent="center" mb={2}>
        <CountBadge label="Wysłane" count={data.submitted} color="warning" />
        <CountBadge label="Zaakceptowane" count={data.accepted} color="success" />
        <CountBadge label="Odrzucone" count={data.rejected} color="error" />
        <CountBadge label="Błąd" count={data.failed} color="error" />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Stany pośrednie i łącznie */}
      <Stack direction="row" spacing={4} justifyContent="center">
        <CountBadge label="Przygotowane" count={data.prepared} />
        <CountBadge label="Utworzone" count={data.created} />
        <CountBadge label="Łącznie" count={data.total} />
      </Stack>

      {(data.rejected > 0 || data.failed > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Alert severity="warning" sx={{ py: 0 }}>
            {data.rejected > 0 && (
              <span>
                {data.rejected} {data.rejected === 1 ? 'faktura odrzucona' : 'faktury odrzucone'} przez KSeF — wymaga decyzji operatora.{' '}
              </span>
            )}
            {data.failed > 0 && (
              <span>
                {data.failed} {data.failed === 1 ? 'faktura z błędem' : 'faktury z błędem'} — sprawdź konsolę KSeF.
              </span>
            )}
          </Alert>
        </>
      )}
    </Paper>
  );
};

export default KsefOutgoingStatusWidget;
